import React, { ReactNode, useEffect } from 'react'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { theme } from '../../../theme/theme'
import { ShortcutCard } from './ShortcutCard'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ButtonVariant } from '../../../components/common/Button'
import {
  managementActiveAccordionItemStateAtom,
  residentActiveAccordionItemStateAtom,
  selectedSocietySidebarMenuItemStateAtom,
} from '../../../components/society/SocietySideBarContent/societySideBarSharedState'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useSocietyLockedForCurrentUser } from '../../../hooks/useSocietyLockedForCurrentUser'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { ErrorView } from '../../error/ErrorView'
import { GetToKnowView } from '../GetToKnowView'
import { GettingStartedView } from '../GettingStartedView'
import { useActiveSocietyGuide } from '../../../components/society/SocietySideBarContent/useGettingStartedCallout'
import { CONSTANTS_SOCIETY_GUIDES } from '../../../api/society-guides'
import { i18nSocietyType } from '../../../i18n'

export const SocietyHome = (): ReactNode => {
  useDocumentTitle(reverseDocumentTitle('society'))
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const userId = useAuthenticatedUserId() as string
  const navigate = useNavigate()
  const isAdmin = isUserAdminInSociety(society, userId)
  const societyLocked = useSocietyLockedForCurrentUser(society)
  const [, setManagementActiveAccordionIndex] = useAtom(
    managementActiveAccordionItemStateAtom
  )
  const [, setResidentActiveAccordionIndex] = useAtom(
    residentActiveAccordionItemStateAtom
  )
  const [, setSelectedMenuItem] = useAtom(
    selectedSocietySidebarMenuItemStateAtom
  )

  const residentListDisabled =
    society?.hiddenSocietyWidgets?.find((w) => w === 'resident-list') || false

  useEffect(() => {
    setSelectedMenuItem(undefined)
    setResidentActiveAccordionIndex(null)
    setManagementActiveAccordionIndex(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const documentsUrl = reverseUrl('management:documents', { id: society?._id })
  const registerUrl = reverseUrl('management:register', { id: society?._id })
  const societyWebsiteUrl = reverseUrl('management:website', {
    id: society?._id,
  })
  const residentListUrl = reverseUrl('society:resident-list', {
    id: society?._id,
  })

  const cardData = [
    ...((isAdmin && [
      {
        title: translate('societyRoot.titles.boardDocuments', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        subtitle: translate('societyRoot.subtitles.boardDocuments', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        url: documentsUrl,
        handleSideBarSelection: () => {
          setSelectedMenuItem(documentsUrl)
        },
      },
      {
        title: translate('societyRoot.titles.register'),
        subtitle: translate('societyRoot.subtitles.register'),
        url: registerUrl,
        handleSideBarSelection: () => {
          setSelectedMenuItem(registerUrl)
        },
      },
      {
        title: translate('societyRoot.titles.societyWebsite', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        subtitle: translate('societyRoot.subtitles.societyWebsite', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        url: societyWebsiteUrl,
        handleSideBarSelection: () => {
          setSelectedMenuItem(societyWebsiteUrl)
        },
      },
    ]) ||
      []),
    ...((!residentListDisabled && [
      {
        title: translate('societyRoot.titles.residentList', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        subtitle: translate('societyRoot.subtitles.residentList', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        url: residentListUrl,
        handleSideBarSelection: () => {
          setSelectedMenuItem(residentListUrl)
        },
      },
    ]) ||
      []),
  ]
  const activeGuide = useActiveSocietyGuide(society)
  if (societyLocked) {
    return (
      <div className="flex w-full flex-col justify-between space-y-4 md:h-full md:space-y-2">
        <div className="flex flex-col">
          <ErrorView
            title={translate('invoiceView.basic.locked.title', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            subtitle={translate('invoiceView.basic.locked.subtitle')}
            illustration={IllustrationChoices.EMPTY}
            className="px-4 py-16"
            buttons={[
              {
                id: 'pay-annual-fee',
                button: {
                  label: translate('common.button.toPayment'),
                  onClick: () =>
                    navigate(
                      reverseUrl('management:billing-payment', {
                        id: society?._id,
                      })
                    ),
                  variant: ButtonVariant.PRIMARY,
                },
              },
            ]}
          />
        </div>
      </div>
    )
  }
  if (activeGuide === CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW) {
    return <GetToKnowView />
  }
  if (activeGuide === CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED) {
    return <GettingStartedView />
  }
  return (
    <div className="flex w-full flex-col justify-between space-y-4 md:h-full md:space-y-2">
      <div className="flex flex-col">
        <p style={theme.textVariants.h2} translate="no">
          {society?.name}
        </p>
        <p className="text-neutral-40" style={theme.textVariants.base}>
          {translate('societyRoot.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
        </p>
        <div className="flex w-full flex-col space-y-2">
          <p className="mt-6" style={theme.textVariants.baseBold}>
            {translate('societyRoot.shortcutTitle', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          </p>
          <div className="flex flex-wrap gap-4">
            {cardData.map((card) => (
              <ShortcutCard key={card.url} {...card} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
